import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View, } from 'react-native';
import { ModalFlatList } from 'design-system/ModalScrollables';
import { rem } from 'design-system/values';
import { useAppSelector } from 'store/hooks';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import useColors from 'hooks/useColors';
import { Button, TextButton } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks';
import { selectHasSpaces } from 'features/spaces/reducers/selectors';
import RadioButton from 'design-system/RadioButton';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { AccountItem } from 'features/spaces/components';
import { isWeb } from '../../../constants';
export const keyExtractor = (item) => item.id.toString();
export const SelectAccountsModalContent = ({ spacesToShow, selectedAccounts, onScroll, onSave, }) => {
    const hasSpaces = useAppSelector(selectHasSpaces);
    const flatlistData = useMemo(() => spacesToShow.reduce((acc, curr) => {
        if (!hasSpaces)
            return [...acc, ...(curr.accounts || [])];
        return [...acc, curr.space, ...(curr.accounts || [])];
    }, []), [spacesToShow, hasSpaces]);
    const [selected, setSelected] = useState(selectedAccounts);
    const onPress = useCallback(() => {
        if (!selected.length)
            return;
        onSave(selected);
        Modal.hide();
    }, [onSave, selected]);
    const getAllVisibleSpaceAccounts = useCallback((spaceId) => spacesToShow
        .find((spaceOrAccount) => spaceOrAccount.space.id === spaceId)
        ?.accounts.filter((account) => !('isEmmaInvestmentAccount' in account || 'productId' in account)) || [], [spacesToShow]);
    const onSelectAll = useCallback((spaceId) => {
        const allSpaceAccounts = getAllVisibleSpaceAccounts(spaceId);
        setSelected((prev) => {
            const selectedIds = new Set(prev.map((acc) => acc.id));
            const newAccounts = allSpaceAccounts.filter((acc) => !selectedIds.has(acc.id));
            return [...prev, ...newAccounts];
        });
    }, [getAllVisibleSpaceAccounts]);
    const onDeselectAll = useCallback((spaceId) => {
        setSelected((prev) => prev.filter((selectedAccount) => selectedAccount.spaceId !== spaceId));
    }, []);
    const getSelectedCountForSpace = useCallback((spaceId) => selected.filter((selectedAccount) => selectedAccount.spaceId === spaceId)
        .length, [selected]);
    const onSelect = useCallback((item) => {
        setSelected((prev) => {
            const index = prev.findIndex((s) => s.id === item.id);
            const isSelected = index !== -1;
            if (isSelected) {
                return prev.filter((_, i) => i !== index);
            }
            return [...prev, item];
        });
    }, []);
    const colors = useColors();
    const renderItem = useCallback(({ item }) => {
        if ('isDefault' in item) {
            const selectedCount = getSelectedCountForSpace(item.id);
            const visibleAccountsCount = getAllVisibleSpaceAccounts(item.id).length;
            const isAllSelected = visibleAccountsCount > 0 && selectedCount === visibleAccountsCount;
            const hasMoreThanOneSpace = spacesToShow.length > 1;
            // eslint-disable-next-line no-nested-ternary
            const title = hasMoreThanOneSpace
                ? item.isDefault
                    ? 'Private space'
                    : item.name
                : 'Accounts';
            return (<View style={styles.selectAllRow}>
            <Text Text-16 Secondary>
              {`${title} (${selectedCount})`}
            </Text>
            <TextButton text={`${isAllSelected ? 'Deselect' : 'Select'} all`} onPress={() => {
                    if (isAllSelected) {
                        onDeselectAll(item.id);
                    }
                    else {
                        onSelectAll(item.id);
                    }
                }}/>
          </View>);
        }
        const isSelected = !!selected.find((s) => s.id === item.id);
        const disabled = 'isEmmaInvestmentAccount' in item || 'productId' in item;
        return (<TouchableHighlight onCard hoverOnLight={!disabled} activeOpacity={1} style={[disabled ? styles.disabled : undefined, styles.touchable]} onPress={disabled ? undefined : () => onSelect(item)} underlayColor={colors.background.underlay}>
          <View style={styles.row} pointerEvents="none">
            <RadioButton type="multiple" isChecked={isSelected}/>
            <View style={styles.accountItem}>
              <AccountItem account={item} disabled/>
            </View>
          </View>
        </TouchableHighlight>);
    }, [
        colors.background.underlay,
        getAllVisibleSpaceAccounts,
        getSelectedCountForSpace,
        onDeselectAll,
        onSelect,
        onSelectAll,
        selected,
        spacesToShow.length,
    ]);
    const ListHeaderComponent = useMemo(() => hasSpaces ? (<Text TextThin-16 Secondary centered={!isWeb} style={styles.headerText}>
          These accounts belong to the spaces you’ve chosen
        </Text>) : null, [hasSpaces]);
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + (isWeb ? 0 : rem(16)));
    return (<>
      <ModalFlatList data={flatlistData} onScroll={onScroll} scrollEventThrottle={16} renderItem={renderItem} style={style} keyExtractor={keyExtractor} contentContainerStyle={contentContainerStyle} ListHeaderComponent={ListHeaderComponent}/>
      <Button brand floating title="Save" disabled={!selected.length} onPress={onPress} containerStyle={styles.button}/>
    </>);
};
const styles = StyleSheet.create({
    button: {
        marginHorizontal: 0,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: isWeb ? rem(8) : rem(16),
    },
    accountItem: {
        flex: 1,
        ...(isWeb && { marginLeft: rem(8) }),
    },
    headerText: {
        marginBottom: rem(8),
        marginHorizontal: rem(16),
    },
    disabled: {
        opacity: 0.5,
        // @ts-expect-error
        cursor: 'default',
    },
    touchable: {
        ...(isWeb && { marginHorizontal: rem(8) }),
    },
    selectAllRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: rem(16),
        paddingBottom: rem(16),
        justifyContent: 'space-between',
        paddingHorizontal: rem(16),
        ...(isWeb && {
            paddingBottom: rem(8),
            paddingRight: rem(4),
        }),
    },
});
